/**
 * Dashboard Metrics Caching Service
 * Provides efficient caching for dashboard statistics using Redis with TTL
 * Now uses query hooks for data fetching to avoid duplicate API calls
 */

import { apiService } from './api';
import { productivityMetrics } from './productivityMetrics';

interface DashboardMetrics {
  totalMemories: number;
  lastUpdated: number;
  // Productivity insights
  productivity?: {
    timeSavedThisWeek: number;
    dollarsaved: number;
    productivityScore: number;
    topInsight: string;
  };
}

interface CacheConfig {
  ttl: number; // Time to live in seconds
  key: string;
}

class DashboardCacheService {
  private static instance: DashboardCacheService;
  private cache = new Map<string, { data: any; expires: number }>();
  
  // Cache configuration
  private readonly CACHE_CONFIG: Record<string, CacheConfig> = {
    dashboard_metrics: { ttl: 300, key: 'dashboard_metrics' }, // 5 minutes
    space_memory_counts: { ttl: 180, key: 'space_memory_counts' }, // 3 minutes
    search_analytics: { ttl: 600, key: 'search_analytics' }, // 10 minutes
  };

  static getInstance(): DashboardCacheService {
    if (!DashboardCacheService.instance) {
      DashboardCacheService.instance = new DashboardCacheService();
    }
    return DashboardCacheService.instance;
  }

  /**
   * Get cached data or fetch if expired/missing
   */
  private async getOrFetch<T>(
    cacheKey: string,
    fetchFunction: () => Promise<T>
  ): Promise<T> {
    const config = this.CACHE_CONFIG[cacheKey];
    if (!config) {
      // No caching for this key, fetch directly
      return fetchFunction();
    }

    const cached = this.cache.get(config.key);
    const now = Date.now();

    // Return cached data if valid
    if (cached && cached.expires > now) {
      console.log(`📦 Cache hit for ${cacheKey}`);
      return cached.data;
    }

    // Fetch fresh data
    console.log(`🔄 Cache miss for ${cacheKey}, fetching fresh data`);
    try {
      const freshData = await fetchFunction();
      
      // Store in cache with expiration
      this.cache.set(config.key, {
        data: freshData,
        expires: now + (config.ttl * 1000)
      });

      return freshData;
    } catch (error) {
      // If fetch fails and we have stale cache, return it
      if (cached) {
        console.warn(`⚠️ Fetch failed for ${cacheKey}, returning stale cache`);
        return cached.data;
      }
      throw error;
    }
  }

  /**
   * Get dashboard metrics with caching
   * Now uses spaces from query hook cache to avoid duplicate API calls
   */
  async getDashboardMetrics(availableSpaces: any[] = []): Promise<DashboardMetrics> {
    return this.getOrFetch('dashboard_metrics', async () => {
      let totalMemoriesCount = 0;
      
      // ✅ Use provided spaces (from useSpacesQuery hook cache) to avoid duplicate API calls
      // If no spaces provided, try to get from query hook cache
      let spacesToUse = availableSpaces;
      if (!spacesToUse || spacesToUse.length === 0) {
        // Try to get from query hook cache (non-blocking)
        try {
          // Import dynamically to avoid circular dependencies
          const { spacesCache } = await import('../hooks/useSpacesQuery');
          const cached = spacesCache.getCache();
          if (cached && cached.spaces) {
            spacesToUse = cached.spaces;
            console.log('✅ Got spaces from query hook cache:', spacesToUse.length, 'spaces');
          }
        } catch (error) {
          console.warn('⚠️ Could not access spaces cache, using empty array');
          spacesToUse = [];
        }
      }
      
      // Sum memory counts from spaces
      totalMemoriesCount = spacesToUse.reduce((sum, space) => {
        const count = space.memory_count || 0;
        console.log(`📊 Space "${space.name}": ${count} memories`);
        return sum + count;
      }, 0);
      
      console.log('✅ Total memories:', totalMemoriesCount);

      // Get productivity insights
      const productivitySummary = await productivityMetrics.getQuickSummary();

      console.log('📊 Dashboard metrics:', {
        totalMemories: totalMemoriesCount,
        productivity: productivitySummary
      });

      return {
        totalMemories: totalMemoriesCount,
        lastUpdated: Date.now(),
        productivity: productivitySummary
      };
    });
  }

  /**
   * Space-scoped weekly Storage vs Retrieval (rolling window from API).
   * Cached per space; cleared on memory changes via onMemoryChange.
   */
  async getWeeklyMemoryActivity(
    spaceId: string
  ): Promise<{ storageThisWeek: number; retrievalThisWeek: number }> {
    const compositeKey = `weekly_activity:${spaceId}`;
    const now = Date.now();
    const ttlMs = 5 * 60 * 1000;
    const cached = this.cache.get(compositeKey);
    if (cached && cached.expires > now) {
      return cached.data as { storageThisWeek: number; retrievalThisWeek: number };
    }
    try {
      const raw = await apiService.getWeeklyMemoryActivity(spaceId);
      const data = {
        storageThisWeek: raw.storage_this_week ?? 0,
        retrievalThisWeek: raw.retrieval_this_week ?? 0,
      };
      this.cache.set(compositeKey, { data, expires: now + ttlMs });
      return data;
    } catch (error) {
      console.warn('⚠️ Failed to fetch weekly memory activity:', error);
      return { storageThisWeek: 0, retrievalThisWeek: 0 };
    }
  }

  /**
   * Get space memory counts with caching
   * Now uses memory query hook cache to avoid duplicate API calls
   */
  async getSpaceMemoryCounts(spaceIds: string[]): Promise<Record<string, number>> {
    return this.getOrFetch('space_memory_counts', async () => {
      const counts: Record<string, number> = {};

      await Promise.all(
        spaceIds.map(async (spaceId) => {
          try {
            const list = await apiService.listMemoriesPage({
              spaceId,
              page: 1,
              size: 1,
            });
            counts[spaceId] = list.total;
          } catch (error: any) {
            console.warn(`Failed to get memory count for space ${spaceId}:`, error.message);
            counts[spaceId] = 0;
          }
        })
      );

      return counts;
    });
  }

  /**
   * Invalidate cache for specific keys
   */
  invalidateCache(keys: string[] = []): void {
    if (keys.length === 0) {
      // Clear all cache
      this.cache.clear();
      console.log('🗑️ Cleared all dashboard cache');
    } else {
      // Clear specific keys
      keys.forEach(key => {
        const config = this.CACHE_CONFIG[key];
        if (config) {
          this.cache.delete(config.key);
          console.log(`🗑️ Cleared cache for ${key}`);
        }
      });
    }
  }

  /**
   * Invalidate cache when memories are created/deleted
   */
  onMemoryChange(): void {
    this.invalidateCache(['dashboard_metrics', 'space_memory_counts']);
    for (const k of [...this.cache.keys()]) {
      if (k.startsWith('weekly_activity:')) {
        this.cache.delete(k);
      }
    }
  }

  /**
   * Invalidate cache when spaces are created/deleted
   */
  onSpaceChange(): void {
    this.invalidateCache(['dashboard_metrics', 'space_memory_counts']);
  }

  /**
   * Invalidate cache when searches are performed
   */
  onSearchPerformed(): void {
    this.invalidateCache(['search_analytics']);
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): Record<string, any> {
    const stats: Record<string, any> = {};
    const now = Date.now();

    Object.entries(this.CACHE_CONFIG).forEach(([key, config]) => {
      const cached = this.cache.get(config.key);
      stats[key] = {
        exists: !!cached,
        expired: cached ? cached.expires < now : null,
        expiresIn: cached ? Math.max(0, cached.expires - now) : null,
        ttl: config.ttl
      };
    });

    return stats;
  }
}

// Export singleton instance
export const dashboardCache = DashboardCacheService.getInstance();

// Export types
export type { DashboardMetrics }; 